import React, { useCallback, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { Workspaces } from "@mui/icons-material";
import { Alert, Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import {
  useCreateVersionCoreConfigurationMutation,
  useCreateVersionScenarioConfigurationMutation,
  useGetProjectQuery,
  useGetVersionSpliceTableQuery,
  useUpdateVersionCoreConfigurationMutation,
  useUpdateVersionScenarioConfigurationMutation,
} from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { getCurrentProject } from "fond/project";
import { Maybe, Store, VersionCoreConfiguration, VersionScenarioConfiguration } from "fond/types";
import { Modal } from "fond/widgets";

import {
  convertConfigToFormData,
  convertFormDataToCoreConfig,
  convertFormDataToScenarioConfig,
  defaultFormValues,
  hasCostToServeArtifacts,
  hasSpliceTables,
} from "./helper";
import SetupForm, { FormData } from "./SetupForm";

interface SetupModalProps {
  coreConfiguration: Maybe<VersionCoreConfiguration>;
  scenarioConfiguration: Maybe<VersionScenarioConfiguration>;
  isReadOnly: boolean;
  onClose: () => void;
}

export const Steps = {
  UPLOAD: 0,
  LAYER_SELECTION: 1,
  COST_TO_SERVE_CONFIGURATION: 2,
  SPLICE_VIEW_CONFIGURATION: 3,
} as const;
export type Steps = (typeof Steps)[keyof typeof Steps];

const ALL_STEPS = ["Upload artifacts", "Associate layers", "Cost to serve configuration", "Splice view configuration"];

const SetupModal: React.FC<SetupModalProps> = ({ coreConfiguration, scenarioConfiguration, onClose, isReadOnly }) => {
  const { value: isCollaborationSpliceEnabled } = useFeatureFlag("collaboration_splice", false);
  // The Splice view configuration step is hidden entirely when the collaboration_splice feature flag is off.
  const steps = isCollaborationSpliceEnabled ? ALL_STEPS : ALL_STEPS.slice(0, Steps.SPLICE_VIEW_CONFIGURATION);
  const lastStep = isCollaborationSpliceEnabled ? Steps.SPLICE_VIEW_CONFIGURATION : Steps.COST_TO_SERVE_CONFIGURATION;
  const [activeStep, setActiveStep] = useState<Steps>(Steps.UPLOAD);
  const [isSaving, setIsSaving] = useState(false);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const currentProject = useSelector((state: Store) => getCurrentProject(state.project));
  const { data: project } = useGetProjectQuery(currentProject?.ID ?? skipToken);
  const { data: existingSpliceTables, isLoading: isSpliceTablesLoading } = useGetVersionSpliceTableQuery(versionId, {
    skip: !isCollaborationSpliceEnabled || !coreConfiguration?.SpliceConfiguration,
  });
  const hasExistingSpliceTables = (existingSpliceTables?.length ?? 0) > 0;
  const [createVersionScenarioConfiguration] = useCreateVersionScenarioConfigurationMutation();
  const [updateVersionScenarioConfiguration] = useUpdateVersionScenarioConfigurationMutation();
  const [createVersionCoreConfiguration] = useCreateVersionCoreConfigurationMutation();
  const [updateVersionCoreConfiguration] = useUpdateVersionCoreConfigurationMutation();

  const initialValues = useMemo(() => {
    if (coreConfiguration || scenarioConfiguration) {
      return convertConfigToFormData(coreConfiguration, scenarioConfiguration, project?.SystemOfMeasurement, project?.Currency);
    }
    return {
      ...defaultFormValues,
      SystemOfMeasurement: project?.SystemOfMeasurement ?? defaultFormValues.SystemOfMeasurement,
      Currency: project?.Currency ?? defaultFormValues.Currency,
    };
  }, [coreConfiguration, scenarioConfiguration, project]);

  const handleOnCancel = useCallback(() => {
    analytics.track("collaboration.cts.setup.cancelled", {
      versionId,
      currentStep: steps[activeStep],
      isReadOnly,
    });
    onClose();
  }, [versionId, activeStep, isReadOnly, onClose, steps]);

  const handleOnSubmit = useCallback(
    async (formData: FormData) => {
      const scenarioPayload = convertFormDataToScenarioConfig(formData);
      const corePayload = convertFormDataToCoreConfig(formData);
      setIsSaving(true);
      const isNew = !coreConfiguration && !scenarioConfiguration;
      const analyticsPayload = {
        versionId,
        isNew,
        hasOccupancyFile: scenarioPayload.Inputs.Occupancy.Type === "file",
        useDefaultBins: formData.DefaultBins,
        binCount: scenarioPayload.ToolConfigurations.CostToServe.BinCount,
        layerMappingsCount: corePayload.LayerMappings.length,
      };

      try {
        const savePromises: Promise<unknown>[] = [
          coreConfiguration
            ? updateVersionCoreConfiguration({ versionId: versionId!, configuration: corePayload }).unwrap()
            : createVersionCoreConfiguration({ versionId: versionId!, configuration: corePayload }).unwrap(),
        ];

        // Only save the scenario (Cost to Serve) configuration if Cost to Serve was actually configured
        if (hasCostToServeArtifacts(formData)) {
          savePromises.push(
            scenarioConfiguration
              ? updateVersionScenarioConfiguration({ versionId: versionId!, configuration: scenarioPayload }).unwrap()
              : createVersionScenarioConfiguration({ versionId: versionId!, configuration: scenarioPayload }).unwrap()
          );
        }

        await Promise.all(savePromises);
        analytics.track("collaboration.cts.configuration.saved", analyticsPayload);
        enqueueSnackbar("Configuration updated", { variant: "success" });
        onClose();
      } catch (error) {
        Sentry.captureException(error);
        enqueueSnackbar(`Failed to ${isNew ? "create" : "update"} configuration`, { variant: "error" });
      } finally {
        setIsSaving(false);
      }
    },
    [
      coreConfiguration,
      scenarioConfiguration,
      createVersionCoreConfiguration,
      createVersionScenarioConfiguration,
      onClose,
      updateVersionCoreConfiguration,
      updateVersionScenarioConfiguration,
      versionId,
    ]
  );

  const handleNext = useCallback(() => {
    setActiveStep((prev) => (prev + 1) as Steps);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => (prev - 1) as Steps);
  }, []);

  const handleFormSubmit = (values: FormData) => {
    if (activeStep < lastStep) {
      handleNext();
    } else {
      handleOnSubmit(values);
    }
  };

  // A skipped optional step is not considered completed.
  const isStepCompleted = (index: number, values: FormData): boolean => {
    const stepPassed = activeStep > index || isReadOnly;
    if (index === Steps.COST_TO_SERVE_CONFIGURATION) return stepPassed && hasCostToServeArtifacts(values);
    if (index === Steps.SPLICE_VIEW_CONFIGURATION) return stepPassed && hasSpliceTables(values, hasExistingSpliceTables);
    return stepPassed;
  };

  return (
    <Form<FormData>
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, values }) => (
        <Modal
          open
          disableBackdropClick
          variant="primary"
          header="Import"
          headerIcon={<Workspaces />}
          headerSubtitle="COLLABORATION"
          onClose={handleOnCancel}
          showClose
          showFullscreen
          data-testid="cost-to-serve-settings"
          PaperProps={{ style: { minHeight: "60vh", minWidth: "60vw", maxHeight: "90vh", display: "flex", flexDirection: "column" } }}
          DialogContentProps={{
            sx: {
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            },
          }}
          content={
            <>
              {isReadOnly && (
                <Box mb={2}>
                  <Alert severity="info">This configuration is read-only.</Alert>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 4, flex: 1, minHeight: 0 }}>
                <Box sx={{ minWidth: 175 }}>
                  <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((label, index) => (
                      <Step key={label} completed={isStepCompleted(index, values)}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
                <Box sx={{ flex: 1, overflowY: "auto" }} className="customScrollbars">
                  <SetupForm
                    activeStep={activeStep}
                    isReadOnly={isReadOnly}
                    hasExistingSpliceTables={hasExistingSpliceTables}
                    isSpliceTablesLoading={isSpliceTablesLoading}
                    isSpliceViewEnabled={isCollaborationSpliceEnabled}
                    onSubmit={handleSubmit}
                  />
                </Box>
              </Box>
            </>
          }
          actions={
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <Button onClick={handleOnCancel} data-testid="close-button">
                Close
              </Button>
              <Box>
                {activeStep !== Steps.UPLOAD && (
                  <Button onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                )}
                {activeStep === lastStep && isReadOnly ? (
                  <Button variant="contained" onClick={handleOnCancel} data-testid="readonly-finish-button">
                    Finish
                  </Button>
                ) : (
                  <LoadingButton variant="contained" form="collaboration-cts-form" type="submit" loading={isSaving} disabled={isSpliceTablesLoading}>
                    {activeStep === lastStep ? "Finish" : "Next"}
                  </LoadingButton>
                )}
              </Box>
            </Box>
          }
        />
      )}
    />
  );
};

export default SetupModal;
